import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { termsService } from '../services/termsService';
import type { TermsDocumentStatus } from '../types/terms';

// SW-91: the digital partner onboarding consent gate — mirrors
// BusinessProfileContext.tsx's fetch-on-mount/refetch shape exactly.

type TermsContextType = {
  needsTermsAcceptance: boolean;
  documents: TermsDocumentStatus[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

const TermsContext = createContext<TermsContextType | undefined>(undefined);

export const TermsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [needsTermsAcceptance, setNeedsTermsAcceptance] = useState(false);
  const [documents, setDocuments] = useState<TermsDocumentStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setNeedsTermsAcceptance(false);
      setDocuments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const status = await termsService.getStatus(token);
      setNeedsTermsAcceptance(status.requiresAcceptance);
      setDocuments(status.documents);
    } catch (err) {
      console.error('Failed to fetch terms status:', err);
      setError('Failed to load terms status');
      // Fail open on a transient error — never lock a partner out of the
      // portal because the status check itself failed.
      setNeedsTermsAcceptance(false);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const value: TermsContextType = {
    needsTermsAcceptance,
    documents,
    isLoading,
    error,
    refetch: fetchStatus,
  };

  return (
    <TermsContext.Provider value={value}>
      {children}
    </TermsContext.Provider>
  );
};

export const useTerms = () => {
  const context = useContext(TermsContext);
  if (context === undefined) {
    throw new Error('useTerms must be used within a TermsProvider');
  }
  return context;
};
